// ============================================================
// REQUEST VALIDATION — swap & spell schedule
// ============================================================
//
// The server is authoritative: every client-originated request must
// be re-checked here before it influences state. Geometry is cheap
// enough to revalidate; anything that depends on the match-3 board
// contents is deferred to M3Board on the client (since the server
// is the one running M3Board in Phase 3+, this file's swap check
// stays purely coordinate-level).

import {
  BATTLE_COLS, BATTLE_ROWS, ELEM, M3_COLS, M3_ROWS, SPELLS,
  pointsToLevel,
} from '../constants.ts';
import type { SpellCommand, SwapRequest, ValidationResult, RoundMatchStats } from './types.ts';

/** SRS §2.7 RequestSwap — geometry validation (bounds + adjacency). */
export function validateSwap(req: SwapRequest): ValidationResult {
  if (
    !Number.isInteger(req.r1) || !Number.isInteger(req.c1) ||
    !Number.isInteger(req.r2) || !Number.isInteger(req.c2)
  ) {
    return { valid: false, reason: 'coordinates must be integers' };
  }
  const inBounds = (r: number, c: number) =>
    r >= 0 && r < M3_ROWS && c >= 0 && c < M3_COLS;
  if (!inBounds(req.r1, req.c1) || !inBounds(req.r2, req.c2)) {
    return { valid: false, reason: 'out of bounds' };
  }
  const manhattan = Math.abs(req.r1 - req.r2) + Math.abs(req.c1 - req.c2);
  if (manhattan !== 1) {
    return { valid: false, reason: 'not adjacent' };
  }
  return { valid: true };
}

/**
 * SRS §2.4 + §3.2 — validate a single spell command against the
 * caster's RoundMatchStats and the elements they've already used.
 *
 * Session 9 — `allowedEls` (optional) is the leader's `spellElements`
 * subset; the spellType must be in that set. Omitted = no leader gate
 * (back-compat for tests / fixtures predating the leader feature; all
 * ELEM ids pass).
 */
export function validateSpellCommand(
  cmd: SpellCommand,
  stats: RoundMatchStats,
  usedEls: number[],
  allowedEls?: ReadonlyArray<number>,
): ValidationResult {
  if (!cmd) return { valid: false, reason: 'missing command' };
  if (cmd.spellType === 0) return { valid: true }; // Pass

  if (cmd.spellType < 1 || cmd.spellType >= ELEM.length) {
    return { valid: false, reason: `invalid spellType ${cmd.spellType}` };
  }
  if (allowedEls && !allowedEls.includes(cmd.spellType)) {
    return { valid: false, reason: `element ${ELEM[cmd.spellType]} not in current leader's spell set` };
  }
  if (usedEls.includes(cmd.spellType)) {
    return { valid: false, reason: `element ${ELEM[cmd.spellType]} already used this round` };
  }

  // Points & Tiers: stats[el] is the player's accumulated points for this
  // element this round. The legal powerLevel is the tier it converts to.
  const availablePoints = stats[cmd.spellType] || 0;
  const maxLevel = pointsToLevel(availablePoints);
  if (maxLevel < 1) {
    return { valid: false, reason: `no ${ELEM[cmd.spellType]} resource this round` };
  }
  if (!Number.isFinite(cmd.powerLevel) || cmd.powerLevel < 1 || cmd.powerLevel > maxLevel) {
    return {
      valid: false,
      reason: `powerLevel ${cmd.powerLevel} out of range (max ${maxLevel} @ ${availablePoints}pts)`,
    };
  }

  const sp = SPELLS[ELEM[cmd.spellType]];
  if (!sp) return { valid: false, reason: 'unknown spell' };

  // Session 10 — effects-list model: every non-Pass spell is positional
  // (its radius defines a hit area around a tile). If the spell defines
  // any effect, it needs a target. We keep the in-bounds integer check
  // when a target IS provided.
  if (cmd.targetPos) {
    const { x, y } = cmd.targetPos;
    if (!Number.isInteger(x) || !Number.isInteger(y)) {
      return { valid: false, reason: 'targetPos must have integer coordinates' };
    }
    if (x < 0 || x >= BATTLE_COLS || y < 0 || y >= BATTLE_ROWS) {
      return { valid: false, reason: 'targetPos out of bounds' };
    }
  } else if (sp.effects && sp.effects.length > 0) {
    return { valid: false, reason: 'positional spell requires targetPos' };
  }

  return { valid: true };
}

/** Validate an entire 3-slot schedule, rejecting duplicate elements across ticks. */
export function validateSchedule(
  schedule: SpellCommand[],
  stats: RoundMatchStats,
  allowedEls?: ReadonlyArray<number>,
): ValidationResult {
  if (!Array.isArray(schedule) || schedule.length !== 3) {
    return { valid: false, reason: 'schedule must have 3 slots' };
  }
  const seenEls: number[] = [];
  for (let i = 0; i < 3; i++) {
    const cmd = schedule[i];
    if (!cmd) return { valid: false, reason: `slot ${i} missing` };
    if (cmd.slotIndex !== i) {
      return { valid: false, reason: `slot ${i} has wrong slotIndex ${cmd.slotIndex}` };
    }
    const r = validateSpellCommand(cmd, stats, seenEls, allowedEls);
    if (!r.valid) return { valid: false, reason: `slot ${i}: ${r.reason}` };
    if (cmd.spellType !== 0) seenEls.push(cmd.spellType);
  }
  return { valid: true };
}
